/**
 * Standalone module (no local imports) so `appConfig.ts` can import it
 * without a cycle.
 *
 * Hosts under `insurance.mitti.com` are always Mitti-branded — that is the
 * rebranded domain and needs no toggle.
 *
 * The legacy `care.safetyculture.com` hosts default to Care branding and are
 * controlled by the per-environment toggles below. FG-7447.
 */

// Rebrand cutover toggles for the legacy care.safetyculture.com hosts. Flip
// one to `true` (one PR per environment) to serve Mitti-branded content on
// that old URL.
//
// Only legacy hosts belong here. An insurance.mitti.com entry would have no
// effect — see the precedence below.
const LEGACY_HOST_BRAND_OVERRIDES = new Map<string, boolean>([
  ["app-sandpit.care.safetyculture.com", true], // sandpit — cut over
  ["app.care.safetyculture.com", false], // production
]);

const hostname = typeof window !== "undefined" ? window.location.hostname : "";

// The rebranded domain is Mitti unconditionally — it is checked first so no
// toggle can ever switch it back to Care branding. The toggles only promote a
// legacy host to Mitti; they can never demote.
const isRebrandedDomain = hostname.includes("insurance.mitti.com");
const legacyHostIsCutOver = LEGACY_HOST_BRAND_OVERRIDES.get(hostname) ?? false;

export const isMittiBrand = isRebrandedDomain || legacyHostIsCutOver;

export const brand = {
  isMittiBrand,
  fullName: isMittiBrand ? "Mitti Insurance" : "SafetyCulture Care",
  shortName: isMittiBrand ? "Mitti Insurance" : "Care",
  pageTitle: isMittiBrand ? "Mitti Insurance" : "SafetyCulture Care",
  // The parent company (SafetyCulture) is rebranding to Mitti. Use this for
  // references to the parent company / account-creation-and-login identity
  // (e.g. "create an account with ___", "___'s Terms and Conditions") — NOT
  // for the SafetyCulture inspection app/product itself, which keeps its name
  // (e.g. "the SafetyCulture app", "SafetyCulture inspection", "SafetyCulture
  // ID").
  parentName: isMittiBrand ? "Mitti" : "SafetyCulture",
} as const;
